import React, { useState, useEffect, useRef } from "react";
import {
  AppBar,
  Box,
  Button,
  Typography,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  useMediaQuery,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import UpgradeIcon from '@mui/icons-material/Upgrade';
import LockResetIcon from '@mui/icons-material/LockReset';
import LogoutIcon from '@mui/icons-material/Logout';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import logo from "../t-accounts-logo.svg";
import { useAuth } from "../contexts/AuthContext";
import { useAdminAccess } from "contexts/AdminAccessContext";
import { getAnonymousSession } from "../lib/anonymousSession";
import FeedbackButton from "./FeedbackButton";
import SettingsDialog from './SettingsDialog';
import { useApi } from '../hooks/useApi';
import { NARROW_PORTRAIT_MEDIA_QUERY } from '../lib/responsive';

export default function Banner({ noFixed = false }) {
  const isNarrowPortrait = useMediaQuery(NARROW_PORTRAIT_MEDIA_QUERY);
  const appVersion = process.env.REACT_APP_VERSION || '0.0.0';
  const [showShadow, setShowShadow] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [userPlan, setUserPlan] = useState('Free');
  const [userPlanCode, setUserPlanCode] = useState('free');
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const settingsButtonRef = useRef<HTMLButtonElement | null>(null);
  const { user, signOut } = useAuth();
  const { isAdmin, isLoading: adminLoading } = useAdminAccess();
  const { getBillingStatus } = useApi();
  const navigate = useNavigate();
  const location = useLocation();
  const menuOpen = Boolean(anchorEl);
  const anonymousSession = getAnonymousSession();
  const isHomePage = location.pathname === '/' && !user && !anonymousSession;
  const isDocumentsPage = location.pathname === '/documents';
  const hasAccountSession = Boolean(user || (anonymousSession?.userId && anonymousSession?.accountId));
  const showMyDocumentsButton = hasAccountSession && !isDocumentsPage;
  const showAdminButton = Boolean(user) && !adminLoading && isAdmin;

  useEffect(() => {
    const handleScroll = () => {
      setShowShadow(window.scrollY > 0);
    };
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    let active = true;

    const loadPlan = async () => {
      if (!user) {
        setUserPlan('Free');
        setUserPlanCode('free');
        return;
      }

      const result = await getBillingStatus({}, { shouldShowError: () => false });
      if (!active) {
        return;
      }

      if (result.ok) {
        setUserPlan(result.data.display_name || 'Free');
        setUserPlanCode((result.data.plan_code || 'free').toLowerCase());
      }
    };

    loadPlan();

    return () => {
      active = false;
    };
  }, [getBillingStatus, user]);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleMenuClose();
    await signOut();
    navigate('/');
  };

  const handleUpgrade = () => {
    handleMenuClose();
    navigate('/plans');
  };

  const handleMyPlan = () => {
    handleMenuClose();
    navigate('/plans');
  };

  const handleResetPassword = () => {
    handleMenuClose();
    navigate('/auth?mode=forgot');
  };

  const handleOpenSettingsDialog = (event: React.MouseEvent<HTMLElement>) => {
    // Move focus off the trigger before MUI applies aria-hidden to the app root.
    event.currentTarget.blur();
    handleMenuClose();
    setSettingsDialogOpen(true);
  };

  const handleCloseSettingsDialog = () => {
    setSettingsDialogOpen(false);
  };

  return (
    <>
      <AppBar
        position={noFixed ? "static" : "fixed"}
        data-testid="app-banner"
        style={{
          height: "60px",
          boxShadow: showShadow ? "rgba(9, 30, 66, 0.15) 0px 0.5rem 1rem 0px" : "none",
          transition: "box-shadow 0.5s ease 0s",
        }}
      >
        <Box width="100%" height="100%" bgcolor="#fff" display="flex" justifyContent="center" alignItems="center">
          <Box
            maxWidth="1300px"
            width="100%"
            bgcolor="#fff"
            display="flex"
            alignItems="center"
            pl={isNarrowPortrait ? "12px" : "24px"}
            style={{ height: "100%" }}
          >
            <Box
              data-testid="banner-brand"
              display="flex"
              alignItems="center"
              height="100%"
            >
              <img
                src={logo}
                alt="T-Accounts Logo"
                style={{ height: isNarrowPortrait ? 32 : 40, display: "block", cursor: "pointer" }}
                onClick={() => window.location.href = "/"}
              />
              {!isNarrowPortrait && (
                <>
                  <Typography style={{
                    padding: "3px 7px 2px 7px",
                    borderRadius: "5px",
                    backgroundColor: "#f57c00",
                    color: "#fff",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    marginLeft: "8px"
                  }}>Beta</Typography>
                  <Typography style={{
                    fontSize: "11px",
                    color: "#666",
                    marginLeft: "6px",
                    fontWeight: 500
                  }}>V{appVersion}</Typography>
                </>
              )}
            </Box>
            <Box flexGrow={1} />
            <Box
              data-testid="banner-actions"
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
              height="100%"
              minWidth={0}
            >
            {user ? (
              <>
                {!isNarrowPortrait && (
                  <FeedbackButton
                    isAuthenticated={!!user}
                    hidden={isHomePage}
                    style={{ marginRight: "10px" }}
                  />
                )}
                {showMyDocumentsButton && !isNarrowPortrait && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DescriptionOutlinedIcon />}
                    onClick={() => navigate('/documents')}
                    style={{
                      color: "#1976d2",
                      borderColor: "#1976d2",
                      marginRight: "10px",
                      textTransform: "none",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    My Documents
                  </Button>
                )}
                {!isNarrowPortrait && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => navigate('/prompt-commands')}
                    style={{
                      color: "#1976d2",
                      borderColor: "#1976d2",
                      marginRight: "10px",
                      textTransform: "none",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Prompt Commands
                  </Button>
                )}
                {hasAccountSession && !isNarrowPortrait && (
                  <Tooltip title="Settings">
                    <IconButton
                      ref={settingsButtonRef}
                      size="small"
                      onClick={handleOpenSettingsDialog}
                      sx={{
                        border: '1px solid #1976d2',
                        borderRadius: '6px',
                        color: '#1976d2',
                        marginRight: '10px',
                        padding: '6px',
                      }}
                      aria-label="Open settings"
                    >
                      <SettingsOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {showAdminButton && !isNarrowPortrait && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => navigate('/admin')}
                    style={{
                      color: "#1976d2",
                      borderColor: "#1976d2",
                      marginRight: "10px",
                      textTransform: "none",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Admin
                  </Button>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleMenuOpen}
                  style={{
                    color: "#1976d2",
                    borderColor: "#1976d2",
                    marginRight: "24px",
                    textTransform: "none",
                    padding: "6px 12px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    minWidth: isNarrowPortrait ? "auto" : "180px"
                  }}
                >
                  <Typography style={{ fontSize: "0.875rem", fontWeight: 600, lineHeight: 1.2 }}>
                    {user.email}
                  </Typography>
                  <Typography style={{ fontSize: "0.75rem", color: "#666", lineHeight: 1.2 }}>
                    {userPlan} plan
                  </Typography>
                </Button>
                <Menu
                  anchorEl={anchorEl}
                  open={menuOpen}
                  onClose={handleMenuClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                >
                  {isNarrowPortrait && showMyDocumentsButton && (
                    <MenuItem onClick={() => {
                      handleMenuClose();
                      navigate('/documents');
                    }}>
                      My Documents
                    </MenuItem>
                  )}
                  {isNarrowPortrait && (
                    <MenuItem onClick={() => {
                      handleMenuClose();
                      navigate('/prompt-commands');
                    }}>
                      Prompt Commands
                    </MenuItem>
                  )}
                  {isNarrowPortrait && hasAccountSession && (
                    <MenuItem onClick={handleOpenSettingsDialog}>
                      Settings
                    </MenuItem>
                  )}
                  {isNarrowPortrait && showAdminButton && (
                    <MenuItem onClick={() => {
                      handleMenuClose();
                      navigate('/admin');
                    }}>
                      Admin
                    </MenuItem>
                  )}
                  {userPlanCode === 'free' ? (
                    <MenuItem onClick={handleUpgrade}>
                      <UpgradeIcon fontSize="small" style={{ marginRight: '8px' }} />
                      Upgrade
                    </MenuItem>
                  ) : (
                    <MenuItem onClick={handleMyPlan}>My plan</MenuItem>
                  )}
                  <MenuItem onClick={handleResetPassword}>
                    <LockResetIcon fontSize="small" style={{ marginRight: '8px' }} />
                    Reset password
                  </MenuItem>
                  <MenuItem onClick={handleLogout}>
                    <LogoutIcon fontSize="small" style={{ marginRight: '8px' }} />
                    Log out
                  </MenuItem>
                </Menu>
              </>
            ) : (
              isNarrowPortrait ? (
                <>
                  <IconButton
                    size="small"
                    onClick={handleMenuOpen}
                    sx={{
                      border: '1px solid #1976d2',
                      borderRadius: '6px',
                      color: '#1976d2',
                      marginRight: '12px',
                      padding: '6px',
                    }}
                    aria-label="Open navigation menu"
                  >
                    <MenuIcon fontSize="small" />
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    open={menuOpen}
                    onClose={handleMenuClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  >
                    {showMyDocumentsButton && (
                      <MenuItem onClick={() => {
                        handleMenuClose();
                        navigate('/documents');
                      }}>
                        My Documents
                      </MenuItem>
                    )}
                    <MenuItem onClick={() => {
                      handleMenuClose();
                      navigate('/prompt-commands');
                    }}>
                      Prompt Commands
                    </MenuItem>
                    {hasAccountSession && (
                      <MenuItem onClick={handleOpenSettingsDialog}>
                        Settings
                      </MenuItem>
                    )}
                    <MenuItem onClick={() => {
                      handleMenuClose();
                      navigate('/auth?mode=signin');
                    }}>
                      Log In
                    </MenuItem>
                    <MenuItem onClick={() => {
                      handleMenuClose();
                      navigate('/auth?mode=signup');
                    }}>
                      Sign Up
                    </MenuItem>
                  </Menu>
                </>
              ) : (
                <Box display="flex" alignItems="center" gap={1.5} marginRight="24px">
                  <FeedbackButton isAuthenticated={!!user} hidden={isHomePage} />
                  {showMyDocumentsButton && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DescriptionOutlinedIcon />}
                    onClick={() => navigate('/documents')}
                    style={{
                      color: "#1976d2",
                      borderColor: "#1976d2",
                      fontWeight: 600,
                      textTransform: "none",
                      whiteSpace: "nowrap",
                    }}
                  >
                    My Documents
                  </Button>
                )}
                  <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate('/prompt-commands')}
                  style={{
                    color: "#1976d2",
                    borderColor: "#1976d2",
                    fontWeight: 600,
                    textTransform: "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  Prompt Commands
                </Button>
                  {hasAccountSession && (
                  <Tooltip title="Settings">
                    <IconButton
                      ref={settingsButtonRef}
                      size="small"
                      onClick={handleOpenSettingsDialog}
                      sx={{
                        border: '1px solid #1976d2',
                        borderRadius: '6px',
                        color: '#1976d2',
                        padding: '6px',
                      }}
                      aria-label="Open settings"
                    >
                      <SettingsOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                  <Button
                  variant="outlined"
                  size="small"
                  style={{ color: "#1976d2", borderColor: "#1976d2", fontWeight: 600 }}
                  onClick={() => navigate("/auth?mode=signin")}
                >
                  Log In
                </Button>
                  <Button
                  variant="contained"
                  size="small"
                  style={{ backgroundColor: "#1976d2", color: "#fff", fontWeight: 600 }}
                  onClick={() => navigate("/auth?mode=signup")}
                >
                  Sign Up
                </Button>
                  {(() => {
                    return anonymousSession?.userId && anonymousSession?.accountId ? (
                      <Typography variant="caption" color="text.secondary" style={{ fontSize: "0.7rem", maxWidth: "180px", lineHeight: 1.2 }}>
                        Data managed locally, log in or sign up to not lose any work.
                      </Typography>
                    ) : null;
                  })()}
                </Box>
              )
            )}
            </Box>
          </Box>
        </Box>
      </AppBar>

      <SettingsDialog open={settingsDialogOpen} onClose={handleCloseSettingsDialog} />

    </>
  );
}
